import { Configuration } from '@azure/msal-browser';

// MSAL configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${process.env.NEXT_PUBLIC_AZURE_AD_TENANT_ID || ''}`,
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '',
    postLogoutRedirectUri: typeof window !== 'undefined' ? `${window.location.origin}/login?message=You%20have%20been%20logged%20out` : '',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0:
            console.error(message);
            return;
          case 1:
            console.warn(message);
            return;
          case 2:
            console.info(message);
            return;
          case 3:
            console.debug(message);
            return;
          default:
            return;
        }
      },
      piiLoggingEnabled: false
    }
  }
};

// Default scopes for authentication
export const defaultScopes = ['User.Read'];

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest = {
  scopes: ["User.Read", "User.ReadBasic.All"]
};

// Add here the endpoints for MS Graph API services you would like to use.
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};

// API scopes for backend communication
export const apiRequest = {
  scopes: [`api://${process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID}/access_as_user`]
};

// This function attempts to acquire an access token silently.
// It should be used within components/hooks where authentication is expected.
// It NO LONGER falls back to interactive methods (popup/redirect).
// Interactive methods should be handled separately (e.g., via useMsal hooks or login buttons).
export const getAccessTokenSilently = async (instance: any, request: any): Promise<string | null> => {
  // Ensure MSAL instance is available
  if (!instance) {
    console.error("MSAL instance is not available for getAccessTokenSilently.");
    return null; // Or throw an error if preferred
  }

  // Try getting the active account, fall back to the first available account
  let account = instance.getActiveAccount();
  if (!account) {
    const allAccounts = instance.getAllAccounts();
    if (allAccounts.length > 0) {
      console.warn("No active account found, using the first available account for silent token acquisition.");
      account = allAccounts[0];
    } else {
      console.warn("No accounts found for silent token acquisition.");
      return null; // No accounts available at all
    }
  }

  // Try different scopes for the API
  // Use environment variable first, then try the common formats used by Django/Azure
  const scopes = [
    // Use environment variable if available
    process.env.NEXT_PUBLIC_API_SCOPE,
    // Try common Azure AD formats
    `api://${process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID}/access_as_user`,
    `api://${process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID}/.default`,
    // Try Graph API scopes as fallback
    'User.Read'
  ].filter(Boolean); // Filter out undefined/empty values

  console.debug(`Available API scopes to try: ${scopes.join(', ')}`);
  
  // We'll try each scope, starting with the default one
  let accessToken: string | null = null;
  let lastError: any = null;
  
  for (const scope of scopes) {
    if (!scope) continue; // Skip empty scopes
    
    console.debug(`Attempting token acquisition with scope: ${scope}`);
    
    try {
      const silentRequest = {
        scopes: [scope],
        account: account,
      };
      
      const response = await instance.acquireTokenSilent(silentRequest);
      accessToken = response.accessToken;
      console.debug(`Token acquisition successful with scope: ${scope}`);
      break; // Exit the loop if successful
    } catch (error: any) {
      console.warn(`Token acquisition failed with scope ${scope}:`, error.errorCode || error.message);
      lastError = error;
      // Continue trying other scopes
    }
  }
  
  // If we have a token, return it
  if (accessToken) {
    // Log token details for debugging (don't log the full token in production)
    if (process.env.NODE_ENV !== 'production') {
      try {
        // Split the token to get the payload
        const tokenParts = accessToken.split('.');
        if (tokenParts.length === 3) {
          // Decode the payload (middle part)
          const payload = JSON.parse(atob(tokenParts[1]));
          console.debug("Token payload for debugging:", {
            aud: payload.aud, // Audience (who the token is intended for)
            iss: payload.iss, // Issuer (who created the token)
            exp: new Date(payload.exp * 1000).toISOString(), // Expiration time
            scopes: payload.scp || payload.scope || '(no scopes)', // Scopes in the token
          });
        }
      } catch (e) {
        console.debug("Token format not as expected for debugging:", e);
      }
    }
    return accessToken;
  }
  
  // Handle errors if all scope attempts failed
  if (lastError?.errorCode === "invalid_resource") {
    console.error("All API resources failed authentication. Please check Azure AD app registration.");
  }

  return null; // Indicate failure to acquire token
};
